/**
 * SEO del sitio público de Cochegest.
 *
 * Única fuente de verdad para rutas, títulos, descripciones y datos
 * estructurados del sitio de marketing. Lo consume el cliente React
 * (actualización de <head> en navegación SPA) y el Worker (inyección de
 * cabeceras SEO en el HTML servido a crawlers). No debe importar React ni
 * ninguna dependencia de navegador: también se empaqueta en el Worker.
 */

export const MARKETING_SITE_ORIGIN = "https://cochegest.com";
export const MARKETING_SITE_NAME = "Cochegest";
export const MARKETING_CONTACT_EMAIL = "hola@cochegest.com";
export const MARKETING_LOCALE = "es-ES";
export const MARKETING_OG_IMAGE_PATH = "/og/cochegest-og-cover.png";
export const MARKETING_OG_IMAGE_WIDTH = "1200";
export const MARKETING_OG_IMAGE_HEIGHT = "630";
export const MARKETING_OG_IMAGE_ALT =
  "Cochegest · la gestión de tu compraventa de vehículos, clara y bajo control";

export interface MarketingFaqItem {
  readonly question: string;
  readonly answer: string;
}

/**
 * Preguntas frecuentes principales. Se muestran en /faq y se publican como
 * datos estructurados FAQPage. Las respuestas deben ser siempre literales y
 * veraces: también las lee Google.
 */
export const MARKETING_FAQ_ITEMS: readonly MarketingFaqItem[] = [
  {
    question: "¿Cuánto cuesta Cochegest?",
    answer:
      "Durante la etapa de lanzamiento puedes crear tu empresa y trabajar con Cochegest sin pagos ni tarjeta: hasta 100 vehículos y 10 personas, con CRM, taller, escaparate online, fotografía asistida e informes de operación incluidos.",
  },
  {
    question: "¿Necesito instalar algún programa?",
    answer:
      "No. Cochegest funciona en el navegador, también desde el móvil, y no requiere instalaciones ni mantenimiento por tu parte. Tus datos quedan guardados y disponibles aunque cambies de equipo.",
  },
  {
    question: "¿Está activa la emisión VERI*FACTU en Cochegest?",
    answer:
      "Todavía no. El motor de registros, encadenamiento y huella está preparado técnicamente, pero la emisión y remisión VERI*FACTU permanecen desactivadas hasta completar la declaración responsable, la configuración del proveedor y la validación técnica y contractual en pruebas.",
  },
  {
    question: "¿Puedo publicar mis vehículos en internet?",
    answer:
      "Sí. Cada compraventa dispone de un escaparate online con sus vehículos publicados, fichas con fotografías, formulario de contacto y la opción de usar un dominio propio. El escaparate se actualiza solo: lo que vendes o reservas deja de mostrarse.",
  },
  {
    question: "¿Cuántas personas pueden trabajar con la misma cuenta?",
    answer:
      "El plan de lanzamiento incluye hasta 10 personas. Cada una accede con su propio usuario y ves solo las tareas de tu función: dirección, ventas, taller u oficina. Los permisos se gestionan por rol y queda historial de los cambios.",
  },
  {
    question: "¿Mis datos están separados de otras empresas?",
    answer:
      "Sí. La información de cada empresa está aislada: nadie fuera de tu organización puede ver tus vehículos, clientes, márgenes ni documentos. Además, cada acceso queda protegido con contraseña y verificación adicional.",
  },
  {
    question: "¿Cuánto tardo en empezar a trabajar?",
    answer:
      "Crear el espacio de tu empresa lleva unos minutos y no pide datos de pago. Dar de alta el primer vehículo es igual de rápido: con la matrícula, las fotografías y la documentación, la ficha queda completa y lista para preparar y publicar.",
  },
  {
    question: "¿Qué pasa con mi información si dejo de usar Cochegest?",
    answer:
      "Tu información sigue siendo tuya. Puedes exportar los datos de tu operación y, si lo solicitas, se eliminan conforme a la política de privacidad y retención publicada en el sitio.",
  },
];

export type MarketingSchemaKind = "home" | "page" | "faq" | "pricing";

export interface MarketingSeoEntry {
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly breadcrumb: string;
  readonly schema: MarketingSchemaKind;
}

export const MARKETING_HOME_PATH = "/";

/** Landings orientadas a palabra clave, enlazadas desde «Soluciones». */
export const MARKETING_SOLUTION_PATHS = [
  "/software-compraventa-coches",
  "/software-concesionarios",
  "/crm-compraventa",
  "/escaparate-online",
  "/facturacion-verifactu",
  "/control-margenes",
] as const;

export const MARKETING_SEO_PAGES: Readonly<Record<string, MarketingSeoEntry>> = {
  "/": {
    path: "/",
    title: "Cochegest · Software de gestión para compraventas de coches",
    description:
      "Gestiona vehículos, clientes, ventas, márgenes y trámites desde un único lugar. Motor VERI*FACTU preparado técnicamente y pendiente de activación externa.",
    breadcrumb: "Inicio",
    schema: "home",
  },
  "/funcionalidades": {
    path: "/funcionalidades",
    title: "Funcionalidades del software · Cochegest",
    description:
      "Stock, CRM, taller, escaparate online, documentos, márgenes y avisos inteligentes. El motor VERI*FACTU sigue desactivado hasta completar su activación externa.",
    breadcrumb: "Funcionalidades",
    schema: "page",
  },
  "/precios": {
    path: "/precios",
    title: "Precios y plan de lanzamiento · Cochegest",
    description:
      "Empieza sin pagos ni tarjeta: hasta 100 vehículos y 10 personas con CRM, taller, escaparate, fotografía e informes incluidos durante el lanzamiento.",
    breadcrumb: "Precios",
    schema: "pricing",
  },
  "/faq": {
    path: "/faq",
    title: "Preguntas frecuentes · Cochegest",
    description:
      "Respuestas claras sobre precios, VeriFactu, escaparate online, usuarios, seguridad de datos y primeros pasos con Cochegest.",
    breadcrumb: "Preguntas frecuentes",
    schema: "faq",
  },
  "/contacto": {
    path: "/contacto",
    title: "Contacto · Cochegest",
    description:
      "Habla con el equipo de Cochegest: resolvemos dudas sobre el producto, la migración de tu compraventa y el plan de lanzamiento.",
    breadcrumb: "Contacto",
    schema: "page",
  },
  "/software-compraventa-coches": {
    path: "/software-compraventa-coches",
    title: "Software para compraventa de coches · Cochegest",
    description:
      "Software para compraventas de coches: controla stock, costes, márgenes, clientes, documentos y publicación de cada vehículo en un solo sistema.",
    breadcrumb: "Software para compraventa de coches",
    schema: "page",
  },
  "/software-concesionarios": {
    path: "/software-concesionarios",
    title: "Software de gestión para concesionarios · Cochegest",
    description:
      "Software de gestión para concesionarios y compraventas con equipo: roles y permisos, comisiones, proceso de venta completo e informes de operación.",
    breadcrumb: "Software para concesionarios",
    schema: "page",
  },
  "/crm-compraventa": {
    path: "/crm-compraventa",
    title: "CRM para compraventas de vehículos · Cochegest",
    description:
      "CRM para compraventas: cada consulta de portales, WhatsApp o correo con responsable, siguiente paso, reserva, contrato, cobro y entrega.",
    breadcrumb: "CRM para compraventas",
    schema: "page",
  },
  "/escaparate-online": {
    path: "/escaparate-online",
    title: "Escaparate online para compraventas · Cochegest",
    description:
      "Publica tu stock en un escaparate online con dominio propio: fichas con fotos, contacto directo y bajas automáticas al vender o reservar.",
    breadcrumb: "Escaparate online",
    schema: "page",
  },
  "/facturacion-verifactu": {
    path: "/facturacion-verifactu",
    title: "Motor VERI*FACTU preparado para compraventas · Cochegest",
    description:
      "Motor VERI*FACTU preparado técnicamente para registros, huella y encadenamiento; emisión y remisión pendientes de activación y validación externa.",
    breadcrumb: "Motor VERI*FACTU",
    schema: "page",
  },
  "/control-margenes": {
    path: "/control-margenes",
    title: "Control de márgenes por vehículo · Cochegest",
    description:
      "Control de márgenes por vehículo: coste real con compra, taller y trámites, margen previsto y real, días en stock y alertas antes de perder dinero.",
    breadcrumb: "Control de márgenes",
    schema: "page",
  },
};

/** Rutas públicas de marketing, en el orden usado por el sitemap. */
export const MARKETING_PAGE_PATHS: readonly string[] = Object.keys(MARKETING_SEO_PAGES);

export function marketingAbsoluteUrl(path: string): string {
  if (path === "/") return `${MARKETING_SITE_ORIGIN}/`;
  return `${MARKETING_SITE_ORIGIN}${path}`;
}

const SOFTWARE_FEATURES = [
  "Gestión de stock y expediente completo por vehículo",
  "CRM comercial con reservas, contratos, cobros y entregas",
  "Taller y preparación con costes imputados al vehículo",
  "Escaparate online con dominio propio",
  "Avisos inteligentes con motivo y siguiente acción",
  "Motor VERI*FACTU preparado, ITP y régimen REBU",
  "Informes de margen, días en stock y capital inmovilizado",
];

/**
 * Datos estructurados JSON-LD de una página pública. Devuelve una lista de
 * nodos schema.org listos para serializar dentro de <script type="application/ld+json">.
 */
export function marketingJsonLd(path: string): ReadonlyArray<Record<string, unknown>> {
  const entry = MARKETING_SEO_PAGES[path];
  if (!entry) return [];
  const url = marketingAbsoluteUrl(path);
  const organizationId = `${MARKETING_SITE_ORIGIN}/#organization`;
  const websiteId = `${MARKETING_SITE_ORIGIN}/#website`;

  const nodes: Array<Record<string, unknown>> = [
    {
      "@type": "Organization",
      "@id": organizationId,
      name: MARKETING_SITE_NAME,
      url: `${MARKETING_SITE_ORIGIN}/`,
      email: MARKETING_CONTACT_EMAIL,
      logo: {
        "@type": "ImageObject",
        url: `${MARKETING_SITE_ORIGIN}/brand/cochegest-higgsfield.png`,
        width: 1024,
        height: 1024,
      },
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: `${MARKETING_SITE_ORIGIN}/`,
      name: MARKETING_SITE_NAME,
      inLanguage: MARKETING_LOCALE,
      publisher: { "@id": organizationId },
    },
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: entry.title,
      description: entry.description,
      inLanguage: MARKETING_LOCALE,
      isPartOf: { "@id": websiteId },
      about: { "@id": organizationId },
    },
  ];

  if (path !== "/") {
    nodes.push({
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: `${MARKETING_SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: entry.breadcrumb, item: url },
      ],
    });
  }

  if (entry.schema === "home") {
    nodes.push({
      "@type": "SoftwareApplication",
      name: MARKETING_SITE_NAME,
      url: `${MARKETING_SITE_ORIGIN}/`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      inLanguage: MARKETING_LOCALE,
      description: entry.description,
      featureList: SOFTWARE_FEATURES,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
        description:
          "Plan de lanzamiento: acceso completo sin pagos ni tarjeta durante la etapa de lanzamiento.",
        url: marketingAbsoluteUrl("/precios"),
      },
    });
  }

  if (entry.schema === "pricing") {
    nodes.push({
      "@type": "Product",
      name: "Cochegest · Plan de lanzamiento",
      description: entry.description,
      brand: { "@id": organizationId },
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url,
        description:
          "Hasta 100 vehículos y 10 personas, con CRM, taller, escaparate online, fotografía asistida e informes de operación.",
      },
    });
  }

  if (entry.schema === "faq") {
    nodes.push({
      "@type": "FAQPage",
      mainEntity: MARKETING_FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    });
  }

  return nodes;
}
